import { Box, CircularProgress } from '@mui/material';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { updateOrganizations } from 'features/userSlice';
import { getToken } from 'utils/tokens';
import {
  clearPendingPostLoginRedirect,
  isOrgAlignedWithPendingRedirect,
  isPendingDeepLinkOrgAlignedWithJwtPhysio,
  parseOrgIdFromRelativePath,
  persistPostLoginRedirect,
  rewriteMemberReportsPathForBraincareAdmin,
  sanitizePostLoginRedirect,
} from 'utils/postLoginRedirect';

function UnsignedPage({ children }) {
  const dispatch = useDispatch();
  const deepLinkOrgsFetchSentRef = useRef(false);
  const [deepLinkOrgFetchFinished, setDeepLinkOrgFetchFinished] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeOrgId = useSelector((s) => s.user.active_organization?.id);
  const isBraincareUser = useSelector((s) => s.user.isBraincareUser);
  const organizations = useSelector((s) => s.user.organizations);
  const profile = useSelector((s) => s.user.profile);

  const accessToken = getToken('access_token');
  const refreshToken = getToken('refresh_token');
  const hasSession = accessToken !== undefined && refreshToken !== undefined;
  const isLoginEntry = location.pathname === '/auth/login'
    || location.pathname === '/auth/direct-login'
    || location.pathname === '/auth/sso/callback';
  const isAcceptTermsEntry = location.pathname === '/auth/accept-terms';
  const mustAcceptTerms = profile?.accept_terms === false;

  useEffect(() => {
    if (!hasSession || isLoginEntry) {
      return;
    }

    if (isAcceptTermsEntry) {
      if (!mustAcceptTerms) {
        navigate('/');
      }
      return;
    }

    navigate('/');
  }, [hasSession, isLoginEntry, isAcceptTermsEntry, mustAcceptTerms, navigate]);

  useLayoutEffect(() => {
    if (!hasSession || !isLoginEntry) {
      deepLinkOrgsFetchSentRef.current = false;
      setDeepLinkOrgFetchFinished(false);
      return undefined;
    }
    const raw = searchParams.get('redirect');
    if (raw == null) {
      setDeepLinkOrgFetchFinished(false);
      return undefined;
    }
    const safe = sanitizePostLoginRedirect(raw);
    if (!safe || parseOrgIdFromRelativePath(safe) == null) {
      setDeepLinkOrgFetchFinished(false);
      return undefined;
    }
    const bcCapable = isBraincareUser || Boolean(profile?.braincare_permissions?.length);
    if (!bcCapable) {
      setDeepLinkOrgFetchFinished(false);
      return undefined;
    }
    if (Array.isArray(organizations) && organizations.length > 0) {
      deepLinkOrgsFetchSentRef.current = false;
      setDeepLinkOrgFetchFinished(false);
      return undefined;
    }
    const orgKnown = activeOrgId != null && activeOrgId !== -1;
    const orgAligned = isOrgAlignedWithPendingRedirect(activeOrgId, safe);
    if (orgKnown && orgAligned) {
      setDeepLinkOrgFetchFinished(false);
      return undefined;
    }
    if (deepLinkOrgsFetchSentRef.current) {
      return undefined;
    }
    deepLinkOrgsFetchSentRef.current = true;
    setDeepLinkOrgFetchFinished(false);
    let cancelled = false;
    dispatch(updateOrganizations()).finally(() => {
      if (!cancelled) setDeepLinkOrgFetchFinished(true);
    });
    return () => {
      cancelled = true;
      deepLinkOrgsFetchSentRef.current = false;
    };
  }, [
    activeOrgId,
    dispatch,
    hasSession,
    isBraincareUser,
    isLoginEntry,
    organizations?.length,
    profile?.braincare_permissions?.length,
    searchParams,
  ]);

  if (hasSession && isLoginEntry) {
    const raw = searchParams.get('redirect');
    if (raw != null) {
      const safe = sanitizePostLoginRedirect(raw);
      if (safe) {
        const orgKnown = activeOrgId != null && activeOrgId !== -1;
        const orgAligned = isOrgAlignedWithPendingRedirect(activeOrgId, safe);
        const redirectSpecifiesOrg = parseOrgIdFromRelativePath(safe) != null;
        const bcCapable = isBraincareUser || Boolean(profile?.braincare_permissions?.length);
        const holdWhileOrgsHydrate =
          redirectSpecifiesOrg &&
          (!orgKnown || !orgAligned) &&
          bcCapable &&
          Array.isArray(organizations) &&
          organizations.length === 0 &&
          !deepLinkOrgFetchFinished;
        if (holdWhileOrgsHydrate) {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '40vh',
              }}
            >
              <CircularProgress aria-busy="true" aria-label="Loading" />
            </Box>
          );
        }
        const canUseBcJwtPhysioShortcut =
          bcCapable && isPendingDeepLinkOrgAlignedWithJwtPhysio(accessToken, safe, organizations);
        // Link targets a concrete org: DirectLogin unless Redux aligns, or JWT physio already matches
        // the link's org_id (PK) via organizations[] — same value as JWT claim, different id space.
        const goDirectLoginForDeepLink =
          redirectSpecifiesOrg &&
          (!orgKnown || !orgAligned) &&
          !canUseBcJwtPhysioShortcut;
        const destination = isBraincareUser
          ? rewriteMemberReportsPathForBraincareAdmin(safe)
          : safe;

        if (goDirectLoginForDeepLink) {
          // DirectLogin + AccountList (password for other org; discard redirect on cancel + confirm).
          persistPostLoginRedirect(raw);
          return <Navigate to="/auth/direct-login" replace />;
        }
        // Fulfilling the deep link here; do not leave a duplicate pending in sessionStorage (would
        // re-fire tryNavigate on the next menu org switch for BC admins).
        clearPendingPostLoginRedirect();
        return <Navigate to={destination} replace />;
      }
    }
    // No usable ?redirect= on the URL: always mount DirectLogin so post-login flows (pending
    // redirect in sessionStorage after org switch, tryNavigateToPendingPostLoginRedirect) can run.
    // A shortcut <Navigate to="/" /> here broke deep links after the user picked the correct org.
    return children;
  }

  return children;
}

export default (component) => <UnsignedPage>{component}</UnsignedPage>;
